import React, { Children, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonBase from 'core/modules/atoms/Button/Button';

type ButtonProps = Readonly<{
  color?: string;
  onClick?: () => void;
  text?: string;
}>;

const Button = ButtonBase as unknown as React.ComponentType<ButtonProps>;

type UseDialogWizardParams = Readonly<{
  buttonTextBack?: string;
  buttonTextFinish?: string;
  buttonTextNext?: string;
  children: React.ReactNode;
  currentStep?: number;
  onCancel?: () => void;
  onComplete?: () => void;
  onStepChange?: (nextStep: number) => void;
  validateStep?: (currentStep: number) => boolean;
}>;

type UseDialogWizardResult = Readonly<{
  customFooterLeft: React.ReactNode;
  handleCancel: () => void;
  handleNext: () => void;
  nextButtonText: string;
  resolvedCurrentStep: number;
  stepIndicatorText: string;
  stepRef: React.RefObject<HTMLDivElement>;
  steps: React.ReactNode[];
}>;

function useDialogWizard({
  buttonTextBack,
  buttonTextFinish,
  buttonTextNext,
  children,
  currentStep,
  onCancel,
  onComplete,
  onStepChange,
  validateStep,
}: UseDialogWizardParams): UseDialogWizardResult {
  const { t } = useTranslation(['actions', 'common']);
  const steps = useMemo(() => Children.toArray(children), [children]);
  const totalSteps = steps.length;

  const [internalCurrentStep, setInternalCurrentStep] = useState<number>(0);
  const isControlled = currentStep !== undefined;
  const resolvedCurrentStep = isControlled ? currentStep : internalCurrentStep;

  const isFirstStep = resolvedCurrentStep === 0;
  const isLastStep = resolvedCurrentStep === totalSteps - 1;

  const stepRef = useRef<HTMLDivElement>(null);
  const isFirstFocusEffect = useRef<boolean>(true);

  useEffect(() => {
    if (isFirstFocusEffect.current) {
      isFirstFocusEffect.current = false;
      return;
    }
    stepRef.current?.focus();
  }, [resolvedCurrentStep]);

  function handleNext(): void {
    if (validateStep && !validateStep(resolvedCurrentStep)) return;
    if (isLastStep) {
      onComplete?.();
      return;
    }

    const nextStep = resolvedCurrentStep + 1;
    if (!isControlled) setInternalCurrentStep(nextStep);
    onStepChange?.(nextStep);
  }

  function handleBack(): void {
    if (isFirstStep) return;
    const prevStep = resolvedCurrentStep - 1;
    if (!isControlled) setInternalCurrentStep(prevStep);
    onStepChange?.(prevStep);
  }

  function handleCancel(): void {
    onCancel?.();
  }

  const resolvedButtonTextBack = buttonTextBack ?? t('actions:Go back');
  const resolvedButtonTextNext = buttonTextNext ?? t('actions:Next');
  const resolvedButtonTextFinish = buttonTextFinish ?? t('actions:Accept');

  const nextButtonText = isLastStep ? resolvedButtonTextFinish : resolvedButtonTextNext;
  const stepIndicatorText = t('common:Step X of Y', {
    current: resolvedCurrentStep + 1,
    steps: totalSteps,
  });

  const customFooterLeft = isFirstStep ? null : (
    <Button color="transparent" onClick={handleBack} text={resolvedButtonTextBack} />
  );

  return {
    customFooterLeft,
    handleCancel,
    handleNext,
    nextButtonText,
    resolvedCurrentStep,
    stepIndicatorText,
    stepRef,
    steps,
  };
}

export default useDialogWizard;
